/**
 * Chunked bulk-delete runner.
 *
 * The server caps each POST /api/admin/(scope)/bulk-delete at 200 ids per
 * request (validated by pydantic). To let admins delete arbitrary
 * selections we slice the id list into ≤200-id batches and dispatch
 * them **sequentially** (never in parallel — the server logic isn't
 * transactional and concurrent batches would race on cascade rules).
 *
 * Contract:
 *   deleter(ids, ctx)           — receives one batch + shared context
 *                                 (bulk_operation_id) and returns the
 *                                 per-batch result body from the API.
 *   onProgress({...})           — fired before each batch so callers
 *                                 can render "Deleting batch X of Y".
 *
 * Return shape (aggregated across all batches):
 *   {
 *     total,               // input ids.length after dedupe
 *     requested,           // sum of requested reported by server
 *     deleted,             // total deleted across all batches
 *     failed,              // total failed across all batches
 *     skipped,             // ids we never sent because a prior batch
 *                          // failed (0 when everything ran)
 *     deleted_ids: [...],
 *     failed_ids: [...],
 *     failures:    [...],  // per-record failure reasons, merged
 *     bulk_operation_id,   // shared id stamped on every audit entry
 *     batches: [ ... ],    // per-batch summaries {index, size, result}
 *     stopped: false,      // true when a batch threw and we halted
 *     failedBatch: null,   // {index, size, error} when stopped
 *   }
 */
export const BATCH_SIZE = 200;

function makeBulkOperationId() {
    // Use the browser's crypto.randomUUID when available, fall back to a
    // timestamp + random suffix which is fine for tracing (it doesn't need
    // to be cryptographically strong).
    const g = typeof window !== "undefined" ? window : {};
    if (g.crypto && typeof g.crypto.randomUUID === "function") {
        return g.crypto.randomUUID();
    }
    return "bulk-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
}

function dedupe(ids) {
    const seen = new Set();
    const out = [];
    for (const raw of ids || []) {
        const id = String(raw || "").trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

export async function runChunkedBulkDelete(ids, deleter, onProgress) {
    const cleanIds = dedupe(ids);
    const total = cleanIds.length;
    const bulk_operation_id = makeBulkOperationId();
    const batches = [];
    let cursor = 0;
    while (cursor < cleanIds.length) {
        batches.push(cleanIds.slice(cursor, cursor + BATCH_SIZE));
        cursor += BATCH_SIZE;
    }
    const totalBatches = batches.length;

    const agg = {
        total,
        requested: 0,
        deleted: 0,
        failed: 0,
        skipped: 0,
        deleted_ids: [],
        failed_ids: [],
        failures: [],
        bulk_operation_id,
        batches: [],
        stopped: false,
        failedBatch: null,
    };

    for (let i = 0; i < totalBatches; i += 1) {
        const batch = batches[i];
        onProgress?.({
            phase: "running",
            batchIndex: i,               // 0-based
            batchNumber: i + 1,          // 1-based, for copy
            totalBatches,
            batchSize: batch.length,
            deletedSoFar: agg.deleted,
            failedSoFar: agg.failed,
            total,
            bulk_operation_id,
        });

        try {
            const result = await deleter(batch, { bulk_operation_id });
            agg.requested += result?.requested ?? batch.length;
            agg.deleted += result?.deleted ?? 0;
            agg.failed += result?.failed ?? 0;
            if (Array.isArray(result?.deleted_ids)) agg.deleted_ids.push(...result.deleted_ids);
            if (Array.isArray(result?.failed_ids)) agg.failed_ids.push(...result.failed_ids);
            if (Array.isArray(result?.failures)) agg.failures.push(...result.failures);
            agg.batches.push({ index: i, number: i + 1, size: batch.length, result });
        } catch (err) {
            // Batch-level failure (HTTP error, network, CSRF, etc.).
            // Per spec: stop further processing, no automatic retry.
            const remaining = batches
                .slice(i + 1)
                .reduce((acc, b) => acc + b.length, 0);
            agg.stopped = true;
            agg.skipped = batch.length + remaining;
            agg.failedBatch = {
                index: i,
                number: i + 1,
                size: batch.length,
                error: extractErrorMessage(err),
            };
            agg.batches.push({
                index: i, number: i + 1, size: batch.length,
                result: null, error: extractErrorMessage(err),
            });
            onProgress?.({
                phase: "stopped",
                batchNumber: i + 1,
                totalBatches,
                deletedSoFar: agg.deleted,
                failedSoFar: agg.failed,
                skipped: agg.skipped,
                total,
                error: agg.failedBatch.error,
                bulk_operation_id,
            });
            return agg;
        }
    }

    onProgress?.({
        phase: "done",
        batchNumber: totalBatches,
        totalBatches,
        deletedSoFar: agg.deleted,
        failedSoFar: agg.failed,
        skipped: agg.skipped,
        total,
        bulk_operation_id,
    });
    return agg;
}

function extractErrorMessage(err) {
    const detail = err?.response?.data?.detail;
    if (detail) {
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join("; ");
        return JSON.stringify(detail);
    }
    return err?.message || "Request failed";
}
